// C-03 / S-08 / S-04 Errors search params (Frontend §6.1, §7.1): validated from the URL with defaults stripped. Date
// presets stay as ids (`range=last7`) and are resolved when the query runs: in the clinic's time zone on C-03 and
// the S-04 tab, in the viewer's on S-08 (API.md §1.4).
import { addDays, BOOKED_PRESETS, presetRange, rangeInstants, type DateRangeValue, type LocalDate } from '@pa/ui'
import { isErrorStep, type ErrorStep } from './steps'

/** C-03's date presets (UserFlows C-03): Any time, Today, Last 7 days, Last 30 days, This month, Last month. */
export const ERROR_PRESETS = BOOKED_PRESETS

export interface ErrorsSearch {
  /** A preset id, or `custom` with `from` / `to` (local days, both inclusive). */
  range?: string
  from?: LocalDate
  to?: LocalDate
  /** An error code (UPPER_SNAKE). */
  code?: string
  /** S-08 only. */
  step?: ErrorStep
  /** S-08 only: a clinic id. */
  clinic?: string
  /** ≥ 2 (1 is stripped). */
  page?: number
  /** Opens the drawer (C-03a). */
  error?: string
}

/** The list query: what #20 / #61 take. */
export interface ErrorListParams {
  from?: string
  to?: string
  code?: string
  step?: ErrorStep
  clinicId?: string
  page?: string
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const DATE = /^\d{4}-\d{2}-\d{2}$/
const CODE = /^[A-Z0-9_]{1,64}$/

const str = (v: unknown): string | undefined => (typeof v === 'string' && v !== '' ? v : undefined)
const uuid = (v: unknown) => {
  const s = str(v)
  return s && UUID.test(s) ? s.toLowerCase() : undefined
}

/** The URL → a valid search. `admin` keeps S-08's Clinic and Step filters (C-03 and the S-04 tab drop them). */
export function parseErrorsSearch(raw: Record<string, unknown>, options: { admin?: boolean } = {}): ErrorsSearch {
  const out: ErrorsSearch = {}
  const range = str(raw.range)
  const from = str(raw.from)
  const to = str(raw.to)
  if (range === 'custom') {
    if (from && to && DATE.test(from) && DATE.test(to) && from <= to) Object.assign(out, { range, from, to })
  } else if (range && range !== 'any' && ERROR_PRESETS.some((p) => p.id === range)) {
    out.range = range
  }
  const code = str(raw.code)
  if (code && CODE.test(code)) out.code = code
  if (options.admin) {
    if (isErrorStep(raw.step)) out.step = raw.step
    const clinic = uuid(raw.clinic)
    if (clinic) out.clinic = clinic
  }
  const page = typeof raw.page === 'number' ? raw.page : Number(str(raw.page))
  if (Number.isInteger(page) && page > 1) out.page = page
  const error = uuid(raw.error)
  if (error) out.error = error
  return out
}

/** The date filter's value from the search. */
export const errorRangeValue = (s: ErrorsSearch): DateRangeValue =>
  s.range === 'custom'
    ? { id: 'custom', ...(s.from && { from: s.from }), ...(s.to && { to: s.to }) }
    : { id: s.range ?? 'any' }

/** Any filter set ("Clear filters" appears). */
export const hasErrorFilters = (s: ErrorsSearch): boolean =>
  s.range !== undefined || s.code !== undefined || s.step !== undefined || s.clinic !== undefined

/** The list query for the search: presets at local midnight in `timeZone` (`to` exclusive). */
export function toErrorListParams(s: ErrorsSearch, timeZone: string, today: LocalDate): ErrorListParams {
  const params: ErrorListParams = {}
  const range =
    s.range === 'custom' && s.from && s.to
      ? { from: s.from, to: addDays(s.to, 1) }
      : s.range
        ? presetRange(s.range, today)
        : null
  if (range) Object.assign(params, rangeInstants(range, timeZone))
  if (s.code) params.code = s.code
  if (s.step) params.step = s.step
  if (s.clinic) params.clinicId = s.clinic
  if (s.page) params.page = String(s.page)
  return params
}

/** A filter change: back to page 1, the drawer closed, unset values dropped. */
export function withFilter(s: ErrorsSearch, patch: Partial<ErrorsSearch>): ErrorsSearch {
  const merged: Record<string, unknown> = { ...s, ...patch, page: undefined, error: undefined }
  return Object.fromEntries(Object.entries(merged).filter(([, v]) => v !== undefined))
}
